"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_INVENTORY_CATEGORIES = void 0;
exports.normalizeLinkKey = normalizeLinkKey;
exports.linkKeyApplies = linkKeyApplies;
exports.hasSearchParameters = hasSearchParameters;
exports.searchConfigConditionApplies = searchConfigConditionApplies;
exports.matchesTableFilter = matchesTableFilter;
exports.filterTablesForSection = filterTablesForSection;
exports.getDefaultSearchConfigSections = getDefaultSearchConfigSections;
exports.resolveSearchConfigSections = resolveSearchConfigSections;
exports.resolveInboundDirect = resolveInboundDirect;
exports.serviceSlots = serviceSlots;
exports.pickServiceSlotTime = pickServiceSlotTime;
exports.hasBookableServiceSlot = hasBookableServiceSlot;
exports.tableOffersService = tableOffersService;
exports.knownServiceNames = knownServiceNames;
exports.filterTimesByService = filterTimesByService;
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const luxon_1 = require("luxon");
const conditions_1 = require("./conditions");
/** Link keys are hand-written into marketing URLs, so compare them forgivingly. */
function normalizeLinkKey(value) {
    const trimmed = value === null || value === void 0 ? void 0 : value.trim().toLowerCase();
    return trimmed ? trimmed : undefined;
}
/**
 * Does the active link key satisfy this rule's `linkKey`?
 *
 * A rule naming no key is unaffected by whichever key is active — that is what keeps keyed rules
 * additive, so adding one never changes what an ordinary visitor sees.
 */
function linkKeyApplies(required, active) {
    const wanted = (Array.isArray(required) ? required : required === undefined ? [] : [required])
        .map(normalizeLinkKey)
        .filter((key) => !!key);
    if (wanted.length === 0) {
        return true;
    }
    const activeKey = normalizeLinkKey(active);
    return !!activeKey && wanted.includes(activeKey);
}
function hasSearchParameters(filters) {
    return filters.groupSize != null || !!filters.date || !!filters.time;
}
const GUEST_KEYS = ['betweenGuests', 'minGuestCount', 'maxGuestCount'];
const DATE_KEYS = ['dates', 'betweenDates', 'daysOfWeek', 'daysOfMonth', 'leadTimeHours'];
const TIME_KEYS = ['betweenTimes'];
/**
 * A rule that tests a parameter the guest didn't provide should not match.
 * conditionApplies would otherwise vacuously pass guest checks (undefined
 * comparisons are false) and unpredictably fail date/time checks (invalid
 * DateTime), so we guard explicitly and conservatively: any reference to a
 * missing parameter anywhere in the condition tree fails the rule.
 */
function referencesMissingParameters(condition, filters) {
    var _a, _b;
    if (!condition) {
        return false;
    }
    if (filters.groupSize == null && GUEST_KEYS.some(key => condition[key] != null)) {
        return true;
    }
    if (!filters.date && DATE_KEYS.some(key => condition[key] != null)) {
        return true;
    }
    if (!filters.time && TIME_KEYS.some(key => condition[key] != null)) {
        return true;
    }
    const clauses = [
        ...((_a = condition.and) !== null && _a !== void 0 ? _a : []),
        ...((_b = condition.or) !== null && _b !== void 0 ? _b : []),
        ...(condition.not ? [condition.not] : [])
    ];
    return clauses.some(clause => referencesMissingParameters(clause, filters));
}
function searchConfigConditionApplies(condition, filters, timezone) {
    var _a, _b;
    if (!condition || (0, isEmpty_1.default)(condition)) {
        return true;
    }
    const { hasParameters, linkKey } = condition, rest = __rest(condition, ["hasParameters", "linkKey"]);
    const paramsPresent = hasSearchParameters(filters);
    if (hasParameters === true && !paramsPresent) {
        return false;
    }
    if (hasParameters === false && paramsPresent) {
        return false;
    }
    // Before the isEmpty short-circuit: a rule whose ONLY condition is a link key must still be
    // able to reject. `linkKey` is also stripped from `rest` because conditionApplies below knows
    // nothing about it and would silently ignore it.
    if (!linkKeyApplies(linkKey, filters.linkKey)) {
        return false;
    }
    if ((0, isEmpty_1.default)(rest)) {
        return true;
    }
    if (referencesMissingParameters(rest, filters)) {
        return false;
    }
    // Fall back to safe values for parameters the condition doesn't reference
    // (guarded above) so conditionApplies can build a valid DateTime.
    const fallbackDate = timezone
        ? luxon_1.DateTime.now().setZone(timezone).toISODate()
        : luxon_1.DateTime.now().toISODate();
    const scenario = {
        groupSize: filters.groupSize,
        date: (_a = filters.date) !== null && _a !== void 0 ? _a : fallbackDate,
        time: (_b = filters.time) !== null && _b !== void 0 ? _b : '00:00'
    };
    return (0, conditions_1.conditionApplies)(rest, scenario, timezone);
}
/**
 * Does this table belong in a section carrying `filter`?
 *
 * Include first (ids OR tags, empty/absent means no constraint), exclude second.
 * Exclusion always wins, so a table named in both lists is out.
 */
function matchesTableFilter(table, filter) {
    var _a, _b, _c, _d, _e, _f;
    if (!filter) {
        return true;
    }
    const hasIncludeList = !!((_a = filter.ids) === null || _a === void 0 ? void 0 : _a.length) || !!((_b = filter.tags) === null || _b === void 0 ? void 0 : _b.length);
    if (hasIncludeList) {
        const included = !!((_c = filter.ids) === null || _c === void 0 ? void 0 : _c.includes(table.id)) ||
            !!((_d = filter.tags) === null || _d === void 0 ? void 0 : _d.some(tag => { var _a; return (_a = table.tags) === null || _a === void 0 ? void 0 : _a.includes(tag); }));
        if (!included) {
            return false;
        }
    }
    if ((_e = filter.excludeIds) === null || _e === void 0 ? void 0 : _e.includes(table.id)) {
        return false;
    }
    if ((_f = filter.excludeTags) === null || _f === void 0 ? void 0 : _f.some(tag => { var _a; return (_a = table.tags) === null || _a === void 0 ? void 0 : _a.includes(tag); })) {
        return false;
    }
    return true;
}
/**
 * Narrow a table list to the ones a section wants, preserving order. Returns the
 * input by reference when there is nothing to apply, so an unfiltered section
 * costs nothing and keeps referential equality for memoised consumers.
 */
function filterTablesForSection(tables, filter) {
    if (!filter || (0, isEmpty_1.default)(filter)) {
        return tables;
    }
    return tables.filter(table => matchesTableFilter(table, filter));
}
exports.DEFAULT_INVENTORY_CATEGORIES = [
    'available',
    'x-sell-time',
    'sold-out',
    'x-sell-dates'
];
function getDefaultSearchConfigSections(restaurant) {
    var _a;
    const sections = [];
    sections.push({ type: 'inventory', categories: exports.DEFAULT_INVENTORY_CATEGORIES });
    if ((_a = restaurant.settings) === null || _a === void 0 ? void 0 : _a.alternativePlatform) {
        sections.push({ type: 'standard-seating' });
    }
    sections.push({ type: 'events' });
    return sections;
}
function resolveSearchConfigSections(restaurant, filters) {
    var _a, _b;
    const rules = (_a = restaurant.settings) === null || _a === void 0 ? void 0 : _a.searchConfig;
    if (rules && rules.length > 0) {
        const ordered = [...rules].sort((a, b) => { var _a, _b; return ((_a = b.priority) !== null && _a !== void 0 ? _a : 0) - ((_b = a.priority) !== null && _b !== void 0 ? _b : 0); });
        for (const r of ordered) {
            if (searchConfigConditionApplies(r.condition, filters, restaurant.timezone)) {
                return (_b = r.sections) !== null && _b !== void 0 ? _b : [];
            }
        }
    }
    return getDefaultSearchConfigSections(restaurant);
}
/**
 * Phone/email to render for an inbound-direct section. Explicit-only: the
 * section's configured values are the source of truth, gated by contactMode.
 * A fully bare legacy section (no mode and no phone/email) falls back to the
 * restaurant's contact info to preserve pre-configuration behaviour.
 */
function resolveInboundDirect(section, restaurant) {
    var _a;
    const isLegacyBare = !section.contactMode && !section.phoneNumber && !section.email;
    if (isLegacyBare) {
        const out = {};
        if (restaurant === null || restaurant === void 0 ? void 0 : restaurant.phoneNumber) {
            out.phone = restaurant.phoneNumber;
        }
        if (restaurant === null || restaurant === void 0 ? void 0 : restaurant.contactEmail) {
            out.email = restaurant.contactEmail;
        }
        return out;
    }
    const mode = (_a = section.contactMode) !== null && _a !== void 0 ? _a : 'both';
    const out = {};
    if ((mode === 'phone' || mode === 'both') && section.phoneNumber) {
        out.phone = section.phoneNumber;
    }
    if ((mode === 'email' || mode === 'both') && section.email) {
        out.email = section.email;
    }
    return out;
}
/**
 * The slots a table offers under one of `services`, earliest first.
 *
 * Deliberately NOT restricted to available slots: a section scoped to "Full Day" should still
 * claim a table that runs Full Day but is sold out on this date, so the cross-sell card lands in
 * the right section instead of vanishing from the page.
 */
function serviceSlots(slots, services) {
    if (!(services === null || services === void 0 ? void 0 : services.length) || !slots) {
        return [];
    }
    return Object.entries(slots)
        .filter(([, slot]) => !!(slot === null || slot === void 0 ? void 0 : slot.service) && services.includes(slot.service))
        .map(([time, slot]) => ({ time, slot }))
        .sort((a, b) => a.time.localeCompare(b.time));
}
/**
 * Which start time represents this table in a service-scoped section.
 *
 * Prefers the time the guest actually searched when that time belongs to the service, so a card
 * does not silently re-label itself; otherwise the earliest bookable start, falling back to the
 * earliest start of any kind so a sold-out table still shows the time it would have run at.
 */
function pickServiceSlotTime(slots, services, preferredTime) {
    var _a;
    const matching = serviceSlots(slots, services);
    if (matching.length === 0) {
        return undefined;
    }
    // The searched time wins only when it is actually bookable in this service. Preferring it
    // regardless would let a card advertise — and navigate to — a sold-out start purely because
    // the guest happened to type it.
    const preferred = preferredTime
        ? matching.find(entry => entry.time === preferredTime)
        : undefined;
    if (preferred === null || preferred === void 0 ? void 0 : preferred.slot.available) {
        return preferred.time;
    }
    return ((_a = matching.find(entry => entry.slot.available)) !== null && _a !== void 0 ? _a : matching[0]).time;
}
/** Does this table have a BOOKABLE slot in one of `services`? */
function hasBookableServiceSlot(slots, services) {
    return serviceSlots(slots, services).some(entry => entry.slot.available);
}
/** Does this table offer any of `services` on the date these slots describe? */
function tableOffersService(slots, services) {
    return serviceSlots(slots, services).length > 0;
}
/**
 * Every distinct service name a venue's config can produce, for admin pickers.
 *
 * Read straight off the stored config — table defaults plus anything an override sets — rather
 * than by evaluating a date, so it lists services the venue runs at any point in the year.
 */
function knownServiceNames(restaurant, tables) {
    var _a, _b;
    const names = new Set();
    const collect = (slots) => {
        for (const slot of Object.values(slots !== null && slots !== void 0 ? slots : {})) {
            if (slot === null || slot === void 0 ? void 0 : slot.service) {
                names.add(slot.service);
            }
        }
    };
    for (const table of tables !== null && tables !== void 0 ? tables : []) {
        collect(table === null || table === void 0 ? void 0 : table.defaultTimeSlots);
    }
    for (const override of (_a = restaurant === null || restaurant === void 0 ? void 0 : restaurant.overrides) !== null && _a !== void 0 ? _a : []) {
        collect((_b = override === null || override === void 0 ? void 0 : override.values) === null || _b === void 0 ? void 0 : _b.defaultTimeSlots);
    }
    return [...names].sort();
}
/**
 * Narrow a list of bookable times to the ones belonging to a service.
 *
 * The times come from the availability service (which knows about bookings) while the service
 * labels come from the loaded slots (which know about config), so this intersects the two rather
 * than trusting either alone — it can never advertise a time availability did not offer.
 *
 * With no service scope the list passes through by reference. With a scope but no slots to check
 * against, nothing is returned: a section that cannot verify a time must not advertise it.
 */
function filterTimesByService(times, slots, services) {
    if (!(services === null || services === void 0 ? void 0 : services.length)) {
        return times !== null && times !== void 0 ? times : [];
    }
    if (!(times === null || times === void 0 ? void 0 : times.length) || !slots) {
        return [];
    }
    // Sorted because availableTimes arrives as unordered Object.keys from the availability
    // service, and the cross-sell card falls back to the FIRST entry when every offered time is
    // in the past — unsorted, that fallback can advertise an earlier time than the one shown.
    return times
        .filter(time => {
        var _a;
        const service = (_a = slots[time]) === null || _a === void 0 ? void 0 : _a.service;
        return !!service && services.includes(service);
    })
        .sort((a, b) => a.localeCompare(b));
}
//# sourceMappingURL=searchConfig.js.map